import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";

import Button from "../Components/Button";
import PageBanner from "../Components/PageBanner";
import emailjs from "@emailjs/browser";
import ReCAPTCHA from "react-google-recaptcha";
import {
  contactInfo as initialContactInfoData,
  servicesData,
} from "../Constants/Data";

export default function Appraisal() {
  const [contactInfo, setContactInfoData] = useState([]);
  const [captchaValue, setCaptchaValue] = useState(null);
  const [captchaError, setCaptchaError] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    service: "",
    selectedOptions: [],
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const handleCaptchaChange = (value) => {
    // console.log("reCAPTCHA Token:", value);
    setCaptchaValue(value); // Save reCAPTCHA token
    setCaptchaError("");
  };

  useEffect(() => {
    setContactInfoData(initialContactInfoData);
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const optionsList = [
    "Current market trends and conditions",
    "Local property values",
    "The appropriate method of sale for your property",
    "Results-oriented advertising strategies",
    "A detailed timeline of the selling process",
  ];

  const validate = (name, value) => {
    let error = "";
    switch (name) {
      case "name":
        error = /^[A-Za-z\s]+$/.test(value) ? "" : "Only alphabets allowed.";
        break;
      case "email":
        error = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)
          ? ""
          : "Invalid email format.";
        break;
      case "phone":
        error = /^\d{10,15}$/.test(value) ? "" : "Invalid phone number.";
        break;
      case "message":
        error = value.trim() ? "" : "Message cannot be empty.";
        break;
      case "service":
        error = value ? "" : "Please select a service.";
        break;
      default:
        break;
    }
    return error;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: validate(name, value) });
  };

  const handleCheckboxChange = (option) => {
    setFormData((prevData) => {
      const isSelected = prevData.selectedOptions.includes(option);
      const updatedOptions = isSelected
        ? prevData.selectedOptions.filter((item) => item !== option)
        : [...prevData.selectedOptions, option];

      return { ...prevData, selectedOptions: updatedOptions };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    let newErrors = {};
    let isValid = true;

    Object.keys(formData).forEach((field) => {
      const error = validate(field, formData[field]);
      if (error) isValid = false;
      newErrors[field] = error;
    });

    setErrors(newErrors);

    // Check if reCAPTCHA is completed
    if (!captchaValue) {
      setCaptchaError("Please complete the reCAPTCHA verification!");
      return;
    }

    // If validation fails, stop form submission
    if (!isValid) return;

    setIsSubmitting(true);

    emailjs
      .send(
        "service_qcl0p89",
        "template_4ul6o07",
        {
          from_name: formData.name,
          from_email: formData.email,
          phone: formData.phone,
          service: formData.service,
          message: formData.message,
          selected_options: formData.selectedOptions.join(", "),
          "g-recaptcha-response": captchaValue,
        },
        "7ilGZ_oY1u8taCJvv"
      )
      .then(
        () => {
          setSuccessMessage("Message sent successfully!");
          setFormData({
            name: "",
            email: "",
            phone: "",
            message: "",
            service: "",
            selectedOptions: [],
          });
          setCaptchaValue(null); // Reset reCAPTCHA after success
          setCaptchaError(""); // Clear any existing reCAPTCHA error
          setIsSubmitting(false);
        },
        (error) => {
          // console.error("Email sending failed:", error);
          setSuccessMessage("Failed to send message. Try again.");
          setIsSubmitting(false);
        }
      );
  };

  return (
    <>
      <Helmet>
        {/* Basic SEO */}
        <title>Request Appraisal | Urban View Real Estate PVT LTD</title>
        <meta
          name="description"
          content="Find affordable homes for sale & rent across Australia with Urban View Real Estate. Explore investment properties in Brisbane, Sydney suburbs, Queensland & Melbourne."
        />
        <meta
          name="keywords"
          content="Affordable Homes for Sale in South Australia, Affordable Homes for Sale in Melbourne, Affordable Homes for Sale in Queensland, Development Sites for Sale in Sydney, Cheap Rent a Home in Penrith, House Buying in Western Australia, Cheap Duplex Home in Tamworth, Affordable duplex Home in Armidale, Houses for Rental in St Marys, Investement Property in Queensland, Best Investment Property Sale in Brisbane, Best Investment Property Sale in Rouse Hill, Box Hill, Marsden Park, Vineyard, Investment Property Buy in Austral, Investment Property Buy in Tallawong, Investment Property Buy in Oran Park, Investment Property Buy in Leppington"
        />

        <meta name="author" content="Urban View Real Estate PVT LTD" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://urbanviewre.com/appraisal" />

        {/* Open Graph */}
        <meta property="og:title" content="Urban View Real Estate PVT LTD" />
        <meta
          property="og:description"
          content="Find affordable homes for sale & rent across Australia with Urban View Real Estate. Explore investment properties in Brisbane, Sydney suburbs, Queensland & Melbourne."
        />
        <meta property="og:site_name" content="Urban View Real Estate" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://urbanviewre.com" />
        <meta
          property="og:image"
          content="https://urbanviewre.com/assets/logo-2-CGgGTWZk.png"
        />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Urban View Real Estate PVT LTD" />
        <meta
          name="twitter:description"
          content="Find affordable homes for sale & rent across Australia with Urban View Real Estate. Explore investment properties in Brisbane, Sydney suburbs, Queensland & Melbourne."
        />
        <meta
          name="twitter:image"
          content="https://urbanviewre.com/assets/logo-2-CGgGTWZk.png"
        />
        <meta name="twitter:site" content="@urbanviewre" />
        <meta name="twitter:creator" content="@urbanviewre" />

        {/* Facebook  */}
        <meta
          property="fb:admins"
          content="https://www.facebook.com/profile.php?id=61559035348971"
        />

        {/* Instagram */}
        <meta name="instagram:title" content="Urban View Real Estate PVT LTD" />
        <meta
          name="instagram:description"
          content="Find affordable homes for sale & rent across Australia with Urban View Real Estate. Explore investment properties in Brisbane, Sydney suburbs, Queensland & Melbourne."
        />
        <meta name="instagram:site" content="@urbanviewrealestate" />
      </Helmet>

      <PageBanner
        title="Request Appraisal"
        breadcrumbs={[
          { label: "Home", href: "/", icon: "bi-house-door-fill" },
          { label: "Request Appraisal", active: true },
        ]}
      />

      <section className="contact bg-white py-5 mb-5">
        <div className="container mb-5">
          <div className="card border-0 rounded-0 shadow p-5">
            <h3 className="fw-bold brand border-start border-3 border-primary ps-3">
              Request Appraisal
            </h3>

            <p className="text-muted mt-3">
              Fill the form and we will contact you with information about:
            </p>
            <ul className="list-unstyled">
              {optionsList.map((item, index) => (
                <li
                  key={index}
                  className="d-flex align-items-center mb-3 text-muted"
                >
                  <input
                    type="checkbox"
                    className="form-check-input me-2"
                    checked={formData.selectedOptions.includes(item)}
                    onChange={() => handleCheckboxChange(item)}
                  />
                  <label className="form-check-label">{item}</label>
                </li>
              ))}
            </ul>

            <form onSubmit={handleSubmit}>
              <div className="row g-3 mt-3">
                <div className="col-md-6 position-relative pb-3">
                  <input
                    type="text"
                    name="name"
                    className={`form-control border rounded-0 py-3 ${
                      errors.name ? "is-invalid" : ""
                    }`}
                    placeholder="Enter your name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                  {errors.name && (
                    <div className="invalid-feedback">{errors.name}</div>
                  )}
                </div>

                <div className="col-md-6 position-relative pb-3">
                  <input
                    type="email"
                    name="email"
                    className={`form-control border rounded-0 py-3 ${
                      errors.email ? "is-invalid" : ""
                    }`}
                    placeholder="Enter email address"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                  {errors.email && (
                    <div className="invalid-feedback">{errors.email}</div>
                  )}
                </div>

                <div className="col-md-6 position-relative py-3">
                  <input
                    type="text"
                    name="phone"
                    className={`form-control border rounded-0 py-3 ${
                      errors.phone ? "is-invalid" : ""
                    }`}
                    placeholder="Enter phone number"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                  {errors.phone && (
                    <div className="invalid-feedback">{errors.phone}</div>
                  )}
                </div>

                <div className="col-md-6 position-relative py-3">
                  <select
                    name="service"
                    className={`form-select border rounded-0 py-3 ${
                      errors.service ? "is-invalid" : ""
                    }`}
                    value={formData.service}
                    onChange={handleChange}
                    required
                  >
                    <option value="" disabled>
                      Select Service Type
                    </option>
                    {servicesData.map((service) => (
                      <option key={service.id} value={service.title}>
                        {service.title}
                      </option>
                    ))}
                  </select>
                  {errors.service && (
                    <div className="invalid-feedback">{errors.service}</div>
                  )}
                </div>

                <div className="col-12 position-relative pb-3">
                  <textarea
                    name="message"
                    className={`form-control border rounded-0 py-3 ${
                      errors.message ? "is-invalid" : ""
                    }`}
                    rows="3"
                    placeholder="Enter message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                  ></textarea>
                  {errors.message && (
                    <div className="invalid-feedback">{errors.message}</div>
                  )}
                </div>

                {/* Google reCAPTCHA v2 */}
                <div className="col-12">
                  <ReCAPTCHA
                    sitekey="6LcVdPsqAAAAAFeo84JcbbftJHjL6T0UKf0KCAjQ" // Replace with your actual site key
                    onChange={handleCaptchaChange}
                  />
                  {captchaError && ( // Show reCAPTCHA error below the box
                    <p className="text-danger mt-2">{captchaError}</p>
                  )}
                </div>

                <div className="col-12">
                  <Button
                    text="GET A FREE SERVICE"
                    className="text-muted"
                    type="submit"
                    disabled={isSubmitting}
                  />
                  {successMessage && (
                    <div
                      className={`alert ${
                        successMessage.includes("")
                          ? "alert-success"
                          : "alert-danger"
                      } mt-3`}
                      role="alert"
                    >
                      {successMessage}
                    </div>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
